import isNumber from 'lodash/isNumber'
import { BigNumberish } from '@ethersproject/bignumber'

import { useMemo } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useAssetsData } from './useAssetsData'
import { useAssetPairPrice } from 'modules/blockChain/hooks/useAssetPairPrice'

import { weiToNum } from 'modules/blockChain/utils/parseWei'
import {
  DEFAULT_DECIMALS,
  getERCAssetByAddress,
} from 'modules/pools/constants/poolAssets'
import type { Loan } from '../types/Loan'

type Args = {
  loan: Loan
  collateralAddress?: string
  totalOutstanding?: number
  totalCollateralAmount?: BigNumberish
}

export function useLoanCollateralCalcs({
  loan,
  collateralAddress,
  totalOutstanding,
  totalCollateralAmount: totalCollateralAmountWei,
}: Args) {
  const { chainId } = useWeb3()
  const { getAssetDecimals } = useAssetsData()

  const { ERC20Address, lt: ltWei } = loan

  const borrowedAsset = getERCAssetByAddress(ERC20Address, chainId)
  const collateralAsset =
    collateralAddress && getERCAssetByAddress(collateralAddress, chainId)
  const collateralDecimals = getAssetDecimals(collateralAsset)

  const borrowedPriceData = useAssetPairPrice(borrowedAsset, 'USD', 1)
  const borrowedPrice =
    borrowedPriceData &&
    weiToNum(borrowedPriceData.price, borrowedPriceData.decimals)

  const collateralPriceData = useAssetPairPrice(collateralAsset, 'USD', 1)
  const collateralPrice =
    collateralPriceData &&
    weiToNum(collateralPriceData.price, collateralPriceData.decimals)

  const liquidationCollateralPrice = useMemo(() => {
    if (
      !collateralAddress ||
      !totalCollateralAmountWei ||
      !isNumber(totalOutstanding) ||
      !borrowedPrice
    ) {
      return
    }

    const totalCollateralAmount = weiToNum(
      totalCollateralAmountWei,
      collateralDecimals,
    )
    const lt = weiToNum(ltWei, DEFAULT_DECIMALS)
    const liquidationTreshold = totalCollateralAmount * (lt / 100)
    const totalOutstandingUSD = totalOutstanding * borrowedPrice

    return totalOutstandingUSD / liquidationTreshold
  }, [
    collateralAddress,
    totalCollateralAmountWei,
    totalOutstanding,
    borrowedPrice,
    collateralDecimals,
    ltWei,
  ])

  return {
    collateralAsset,
    collateralPrice,
    collateralDecimals,
    liquidationCollateralPrice,
  }
}
